import React, { useState } from 'react';
import Button from '../../form/Button';
import html2canvas from 'html2canvas';
import moment from 'moment';
import jsPDF from 'jspdf';

const ExportToPDFButton = props => {
  const text = props.text ? props.text : 'Export to PDF';
  const [loading, setLoading] = useState(false);
  const width = 1680;
  const height = 560;
  const margin = 20;
  const imageSource = require('../../../assets/images/pcmhguide-logo.jpg');

  const setInlineStyles = targetElem => {
    function recurseElementChildren(node) {
      if (!node.style) return;

      const styles = getComputedStyle(node);

      // eslint-disable-next-line no-param-reassign
      node.style.fill = styles.fill;

      for (const child of Array.from(node.childNodes)) {
        recurseElementChildren(child);
      }
    }

    const svgElems = Array.from(targetElem.getElementsByTagName('svg'));

    for (const svgElement of svgElems) {
      recurseElementChildren(svgElement);
    }
  };

  const calculateHeight = (imgProps, width) => {
    return (imgProps.height * width) / imgProps.width;
  };

  const header = pdf => {
    const header = 'NCQA PCMH Initial Recognition Dashboard';
    pdf.text(header, margin, margin);
  };

  const footer = (pdf, paperWidth, paperHeight) => {
    const imageWidth = 50;

    pdf.text('Confidential', margin, paperHeight - margin);

    pdf.text(
      moment().format('MM-DD-YYYY'),
      paperWidth / 2 - margin / 2,
      paperHeight - margin
    );

    try {
      const imgProps = pdf.getImageProperties(imageSource);
      const calculatedImageHeight = calculateHeight(imgProps, imageWidth);
      pdf.addImage(
        imageSource,
        'JPG',
        paperWidth - margin - imageWidth,
        paperHeight - margin - calculatedImageHeight / 2,
        imageWidth,
        calculatedImageHeight
      );
    } catch (error) {
      console.log(`Error adding ${imageSource} to PDF`);
    }
  };

  const exportToPdf = async (elementId, pdfBackgroundColor = '#2c404c') => {
    return new Promise((resolve, reject) => {
      try {
        setLoading(true);
        const organizationDashboard = document.getElementById(elementId);

        // Set the width for the screenshot
        organizationDashboard.setAttribute('style', `width: ${width}px`);

        // Html to canvas
        const options = {
          width,
          height,
          windowWidth: width,
          windowHeight: height,
          backgroundColor: pdfBackgroundColor, // TODO, pull from colors file
          allowTaint: true,
          onclone(cloned) {
            setInlineStyles(cloned.body);
            const svgElements = cloned.body.querySelectorAll('svg');
            svgElements.forEach(item => {
              item.setAttribute(
                'width',
                item.getBoundingClientRect().width.toString()
              );
              item.setAttribute('height', item.getBoundingClientRect().height);
              item.style.width = null;
              item.style.height = null;
            });
          },
        };

        html2canvas(organizationDashboard, options)
          .then(async canvas => {
            // Export to PDF
            await downloadPDF(canvas, resolve);

            // Reset the div to the original width
            organizationDashboard.setAttribute('style', `width:100%`);

            setLoading(false);
            resolve();
          })
          .catch(error => {
            console.log(error);
          })
          .finally(() => setLoading(false));
      } catch (error) {
        console.log(error);
        setLoading(false);
        reject(error);
      }
    });
  };

  const downloadPDF = async canvas => {
    const imgData = canvas.toDataURL('image/jpeg', 1);

    const pdf = new jsPDF({
      orientation: 'landscape',
    });

    // Source image size
    const imgProps = pdf.getImageProperties(imgData);
    const marginSides = 0;

    // PDF Paper size
    const paperWidth = pdf.internal.pageSize.getWidth() - marginSides * 2;
    const paperHeight = pdf.internal.pageSize.getHeight();

    // Calculate
    const calculatedImageHeight = calculateHeight(imgProps, paperWidth);
    const yOffset = Math.abs(paperHeight - calculatedImageHeight) / 2;

    // PDF Content
    header(pdf);
    pdf.addImage(imgData, 'JPG', 0, yOffset, paperWidth, calculatedImageHeight);
    footer(pdf, paperWidth, paperHeight);

    pdf.autoPrint();
    const fileName = props.organizationName
      ? `${props.organizationName}_Dashboard.pdf`
      : 'Dashboard.pdf';
    pdf.save(fileName.replace(/ /g, '-'));
  };

  return (
    <div data-html2canvas-ignore>
      <Button
        isbusy={loading}
        busyText={'Downloading PDF'}
        onClick={() => exportToPdf('OrganizationHeaderMetricsId')}
        icon="fa fa-download"
        defaultText={text}
      />
    </div>
  );
};

export { ExportToPDFButton };
